<template>
  <div class="tool-demo-cover">
    <div
      v-if="isVideoCompare"
      ref="compareBox"
      class="tool-demo-video-compare"
      role="slider"
      tabindex="0"
      :aria-valuenow="Math.round(comparePercent)"
      aria-valuemin="0"
      aria-valuemax="100"
      aria-label="Video comparison"
      :style="{ '--compare-position': comparePercent + '%' }"
      @pointerdown.stop.prevent="startDrag"
      @click.stop.prevent
      @keydown="onKeydown"
    >
      <video
        class="tool-demo-video"
        :src="images[1]"
        muted
        loop
        autoplay
        playsinline
        preload="metadata"
      ></video>
      <div class="tool-demo-video-before" :style="beforeStyle">
        <video
          class="tool-demo-video"
          :src="images[0]"
          muted
          loop
          autoplay
          playsinline
          preload="metadata"
        ></video>
      </div>
      <div class="tool-demo-video-handle" aria-hidden="true">
        <span class="tool-demo-video-line"></span>
        <span class="tool-demo-video-knob">
          <span></span>
          <span></span>
        </span>
      </div>
    </div>
    <template v-else>
      <div
        v-for="(src, idx) in images"
        :key="idx"
        class="tool-demo-segment"
      >
        <video
          v-if="isVideoDemo(src)"
          :src="src"
          muted
          loop
          autoplay
          playsinline
          preload="metadata"
        ></video>
        <img v-else :src="src" :alt="alt" loading="lazy">
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'ToolDemoCover',
  props: {
    images: { type: Array, required: true },
    alt: { type: String, default: '' }
  },
  data: function () {
    return {
      comparePercent: 50,
      dragging: false
    }
  },
  beforeDestroy: function () {
    this.removeDragListeners()
  },
  computed: {
    isVideoCompare: function () {
      return this.images &&
        this.images.length === 2 &&
        this.isVideoDemo(this.images[0]) &&
        this.isVideoDemo(this.images[1])
    },
    beforeStyle: function () {
      var right = 100 - Number(this.comparePercent || 0)
      return { clipPath: 'inset(0 ' + right + '% 0 0)' }
    }
  },
  methods: {
    isVideoDemo: function (src) {
      return /\.mp4($|\?)/i.test(String(src || ''))
    },
    startDrag: function (event) {
      if (!this.isVideoCompare) return
      this.dragging = true
      this.updateCompareFromEvent(event)
      window.addEventListener('pointermove', this.onDragMove, { passive: false })
      window.addEventListener('pointerup', this.stopDrag, { passive: false })
      window.addEventListener('pointercancel', this.stopDrag, { passive: false })
    },
    onDragMove: function (event) {
      if (!this.dragging) return
      if (event && typeof event.preventDefault === 'function') event.preventDefault()
      this.updateCompareFromEvent(event)
    },
    stopDrag: function (event) {
      if (event && typeof event.preventDefault === 'function') event.preventDefault()
      this.dragging = false
      this.removeDragListeners()
    },
    removeDragListeners: function () {
      window.removeEventListener('pointermove', this.onDragMove)
      window.removeEventListener('pointerup', this.stopDrag)
      window.removeEventListener('pointercancel', this.stopDrag)
    },
    updateCompareFromEvent: function (event) {
      var box = this.$refs.compareBox
      if (!box || !event) return
      var rect = box.getBoundingClientRect()
      if (!rect || !rect.width) return
      var x = event.clientX - rect.left
      var pct = Math.max(0, Math.min(100, (x / rect.width) * 100))
      this.comparePercent = pct
    },
    onKeydown: function (event) {
      var key = event && event.key
      if (key !== 'ArrowLeft' && key !== 'ArrowRight') return
      event.preventDefault()
      var delta = key === 'ArrowLeft' ? -5 : 5
      this.comparePercent = Math.max(0, Math.min(100, Number(this.comparePercent || 0) + delta))
    }
  }
}
</script>

<style scoped>
.tool-demo-cover {
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
.tool-demo-segment {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  border-right: 1px solid rgba(255, 255, 255, 0.65);
}
.tool-demo-segment:last-child {
  border-right: none;
}
.tool-demo-segment img,
.tool-demo-segment video,
.tool-demo-video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.tool-demo-video-compare {
  position: relative;
  width: 100%;
  height: 100%;
  background: #111827;
  cursor: ew-resize;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.tool-demo-video-compare:focus { outline: none; }
.tool-demo-video {
  position: absolute;
  inset: 0;
}
.tool-demo-video-before {
  position: absolute;
  inset: 0;
  z-index: 2;
  overflow: hidden;
  pointer-events: none;
}
.tool-demo-video-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  left: var(--compare-position);
  z-index: 3;
  transform: translateX(-50%);
  pointer-events: none;
}
.tool-demo-video-line {
  display: block;
  width: 2px;
  height: 100%;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.14);
}
.tool-demo-video-knob {
  position: absolute;
  top: 50%;
  left: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 28px;
  height: 28px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
}
.tool-demo-video-knob span {
  width: 6px;
  height: 6px;
  border-top: 2px solid #111827;
  border-left: 2px solid #111827;
}
.tool-demo-video-knob span:first-child { transform: rotate(-45deg); }
.tool-demo-video-knob span:last-child { transform: rotate(135deg); }
</style>
